import { useCallback } from 'react'
import { useSignDisputeUpload, useRecordDisputeDocument } from './backend/disputes'
import { writeErrorMessage } from '../utils/writeError'

// 20 MB. Larger than the platform screenshot cap because a CCMA bundle scanned on
// an office multifunction is routinely eight or ten megabytes, and a person who
// has just scanned a referral should not be told to go and compress it.
//
// The edge function refuses to send anything over 3.5 MB to the model, so a file
// between the two limits is stored and shown to a person but not read
// automatically. That is a deliberate gap, the same one the platform board has,
// and the screen says so rather than leaving somebody to wonder why the reading
// came back thin.
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024
export const MAX_READABLE_BYTES = 3.5 * 1024 * 1024

export type UploadOutcome = {
  /** Files whose bytes landed AND whose row was written. Nothing else counts. */
  done: number
  /** One line per file that did not, already written for a person to read. */
  failed: string[]
  /** Chosen but never attempted, because they are over the limit. */
  tooBig: string[]
  /** Stored, but too large for the model to read. Named so the screen can say so. */
  tooBigToRead: string[]
}

/**
 * The three step upload, the same shape as usePlatformUpload and useMarketingUpload
 * and for the same reasons:
 *
 *   1. Ask the Worker for a one-shot signed URL. The Worker composes the storage
 *      path, so neither the path nor the permission is ever the browser's
 *      decision.
 *   2. PUT the bytes straight to Supabase Storage with that URL.
 *   3. Tell the Worker the bytes landed, which is what writes the row.
 *
 * An upload that dies between 2 and 3 leaves an object in the bucket that no row
 * points at, which is invisible and harmless. The other ordering leaves a
 * document in the list that nobody can open, which on this tab would be a piece
 * of paper somebody believes is on file and is not.
 */
export function useDisputeUpload() {
  const sign = useSignDisputeUpload()
  const record = useRecordDisputeDocument()
  const signTrigger = sign.trigger
  const recordTrigger = record.trigger

  const upload = useCallback(
    async (files: File[], matterId: string, docKind: string): Promise<UploadOutcome> => {
      const tooBig = files.filter((f) => f.size > MAX_UPLOAD_BYTES).map((f) => f.name)
      const sendable = files.filter((f) => f.size <= MAX_UPLOAD_BYTES)
      const tooBigToRead = sendable.filter((f) => f.size > MAX_READABLE_BYTES).map((f) => f.name)

      let done = 0
      const failed: string[] = []

      for (const file of sendable) {
        try {
          const signed = (await signTrigger(
            { matter_id: matterId, filename: file.name },
            { skipCache: true },
          )) as { signedUrl?: string; path?: string } | null
          const url = signed?.signedUrl
          if (!url) throw new Error('No upload address came back.')

          const put = await fetch(url, {
            method: 'PUT',
            headers: file.type ? { 'Content-Type': file.type } : undefined,
            body: file,
          })
          if (!put.ok) throw new Error(`Storage refused the file (${put.status}).`)

          await recordTrigger(
            {
              matter_id: matterId,
              storage_path: signed?.path,
              filename: file.name,
              mime_type: file.type || null,
              size_bytes: file.size,
              doc_kind: docKind,
            },
            { skipCache: true },
          )
          done += 1
        } catch (err) {
          failed.push(`${file.name}: ${writeErrorMessage(err)}`)
        }
      }

      return { done, failed, tooBig, tooBigToRead }
    },
    [signTrigger, recordTrigger],
  )

  return { upload }
}
